import { Link, useNavigate } from "react-router-dom"; // J'ai ajouté useNavigate si besoin de rediriger
import { useForm } from "react-hook-form";
import { useState } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import { useTranslation } from "react-i18next";
import { API } from "../../services/axios";
import { URLS } from "../../services/urls";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faEyeSlash, faUser, faEnvelope, faPhone, faLock, faCheckCircle } from "@fortawesome/free-solid-svg-icons";
import Modal from 'react-modal';
import { SignupConfirmation } from "./login-success";
import "../login/LoginPublic.css"; // On réutilise le style public

// Configuration de la modale pour l'accessibilité
if (typeof window !== 'undefined') {
  Modal.setAppElement('body');
}

const schema = (t) =>
  Yup.object({
    firstName: Yup.string().required(t("textRequiredField")),
    lastName: Yup.string().required(t("textRequiredField")),
    phoneNumber: Yup.string().required(t("textRequiredField")),
    email: Yup.string().email(t("textValidEmail")).required(t("textRequiredField")),
    password: Yup.string()
      .required(t("noPassProvided"))
      .min(8, t("passToShort"))
      .matches(/[A-Z]/, t("passUpperCase"))
      .matches(/[a-z]/, t("passLowerCase"))
      .matches(/[0-9]/, t("passNumber"))
      .matches(/[@$!%*?&]/, t("passSpecialChar")),
    confirmPassword: Yup.string()
      .oneOf([Yup.ref("password"), null], t("passMustMatch"))
      .required(t("textRequiredField")),
    agree: Yup.boolean().oneOf([true], t("textRequiredField")) // Validation pour la checkbox
  }).required();

const modalStyles = {
  content: {
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    marginRight: '-50%',
    transform: 'translate(-50%, -50%)',
    maxHeight: '85vh',
    width: '90%',
    maxWidth: '600px',
    padding: '0',
    borderRadius: '16px',
    border: 'none',
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
  },
  overlay: {
    backgroundColor: 'rgba(5, 38, 78, 0.8)',
    zIndex: 9999
  },
};

const Register = () => {
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema(t)),
  });

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const togglePasswordVisibility = () => setShowPassword(!showPassword);
  const toggleConfirmPasswordVisibility = () => setShowConfirmPassword(!showConfirmPassword);

  const onSubmit = (data) => {
    if (!data.agree) {
      toast.error(t("Vous devez accepter les conditions générales pour vous inscrire."));
      return;
    }

    setLoading(true);
    const infos = {
      first_name: data.firstName,
      last_name: data.lastName,
      email: data.email,
      phone_number: data.phoneNumber,
      password: data.password,
      password2: data.confirmPassword,
      is_superuser: false,
    };

    API.post(URLS.AUTHS.REGISTRATION, infos)
      .then((resp) => {
        setLoading(false);
        // On peut appeler la fonction de succès ou rediriger
        SignupConfirmation(t); 
      })
      .catch((error) => {
        console.log(error);
        setLoading(false);
        toast.error(error?.response?.data?.detail || "Une erreur est survenue");
      });
  };

  return (
    <div className="public-login-wrapper">
      <div className="container h-100">
        <div className="row d-flex justify-content-center justify-content-center">
          <div className="col-12 col-md-10 col-lg-8 col-xl-6">
            
            <div className="card public-card shadow-lg animate__animated animate__fadeInUp">
              <div className="card-body p-4 p-md-5">
                
                <div className="text-center mb-4">
                  <img
                    src="assets/imgs/page/homepage2/logo.png"
                    style={{ width: "160px" }}
                    alt="logo"
                    className="mb-3 logo-anim"
                  />
                  <h4 className="fw-bold text-dark">{t("submitNRegister")}</h4>
                  <p className="text-muted small">Créez votre compte en quelques secondes.</p>
                </div>

                <form onSubmit={handleSubmit(onSubmit)}>
                  
                  {/* Ligne Nom / Prénom */}
                  <div className="row">
                    <div className="col-md-6 mb-3">
                        <div className="form-group">
                            <label className="form-label small fw-bold text-muted">{t("firstName")} *</label>
                            <div className="input-group input-group-custom">
                                <span className="input-group-text"><FontAwesomeIcon icon={faUser} className="text-muted"/></span>
                                <input
                                    className={`form-control ${errors.firstName ? 'is-invalid' : ''}`}
                                    type="text"
                                    placeholder="Prénom"
                                    {...register("firstName")}
                                />
                            </div>
                            <small className="text-danger">{errors.firstName?.message}</small>
                        </div>
                    </div>
                    <div className="col-md-6 mb-3">
                        <div className="form-group">
                            <label className="form-label small fw-bold text-muted">{t("lastName")} *</label>
                            <div className="input-group input-group-custom">
                                <span className="input-group-text"><FontAwesomeIcon icon={faUser} className="text-muted"/></span>
                                <input
                                    className={`form-control ${errors.lastName ? 'is-invalid' : ''}`}
                                    type="text"
                                    placeholder="Nom"
                                    {...register("lastName")}
                                />
                            </div>
                            <small className="text-danger">{errors.lastName?.message}</small>
                        </div>
                    </div>
                  </div>

                  {/* Email */}
                  <div className="form-group mb-3">
                    <label className="form-label small fw-bold text-muted">{t("email")} *</label>
                    <div className="input-group input-group-custom">
                        <span className="input-group-text"><FontAwesomeIcon icon={faEnvelope} className="text-muted"/></span>
                        <input
                            className={`form-control ${errors.email ? 'is-invalid' : ''}`}
                            type="email"
                            placeholder="exemple@email.com"
                            {...register("email")}
                        />
                    </div>
                    <small className="text-danger">{errors.email?.message}</small>
                  </div>

                  {/* Téléphone */}
                  <div className="form-group mb-3">
                    <label className="form-label small fw-bold text-muted">{t("phoneNumber")} *</label>
                    <div className="input-group input-group-custom">
                        <span className="input-group-text"><FontAwesomeIcon icon={faPhone} className="text-muted"/></span>
                        <input
                            className={`form-control ${errors.phoneNumber ? 'is-invalid' : ''}`}
                            type="text"
                            placeholder="699..."
                            {...register("phoneNumber")}
                        />
                    </div>
                    <small className="text-danger">{errors.phoneNumber?.message}</small>
                  </div>

                  {/* Mots de passe */}
                  <div className="row">
                    <div className="col-md-6 mb-3">
                        <div className="form-group">
                            <label className="form-label small fw-bold text-muted">{t("password")} *</label>
                            <div className="input-group input-group-custom">
                                <span className="input-group-text"><FontAwesomeIcon icon={faLock} className="text-muted"/></span>
                                <input
                                    className={`form-control ${errors.password ? 'is-invalid' : ''}`}
                                    type={showPassword ? "text" : "password"}
                                    placeholder="******"
                                    {...register("password")}
                                />
                                <button type="button" className="btn btn-link text-muted" onClick={togglePasswordVisibility}>
                                    <FontAwesomeIcon icon={showPassword ? faEye : faEyeSlash} />
                                </button>
                            </div>
                            <small className="text-danger">{errors.password?.message}</small>
                        </div>
                    </div>
                    <div className="col-md-6 mb-3">
                        <div className="form-group">
                            <label className="form-label small fw-bold text-muted">{t("confirmPassword")} *</label>
                            <div className="input-group input-group-custom">
                                <span className="input-group-text"><FontAwesomeIcon icon={faLock} className="text-muted"/></span>
                                <input
                                    className={`form-control ${errors.confirmPassword ? 'is-invalid' : ''}`}
                                    type={showConfirmPassword ? "text" : "password"}
                                    placeholder="******"
                                    {...register("confirmPassword")}
                                />
                                <button type="button" className="btn btn-link text-muted" onClick={toggleConfirmPasswordVisibility}>
                                    <FontAwesomeIcon icon={showConfirmPassword ? faEye : faEyeSlash} />
                                </button>
                            </div>
                            <small className="text-danger">{errors.confirmPassword?.message}</small>
                        </div>
                    </div>
                  </div>

                  {/* Checkbox Conditions */}
                  <div className="form-group mb-4">
                    <div className="d-flex align-items-start">
                        <div className="me-2">
                             <input 
                                type="checkbox" 
                                className="form-check-input mt-1 cursor-pointer" 
                                id="agree"
                                {...register("agree")} 
                             />
                        </div>
                        <div>
                            <label htmlFor="agree" className="small text-muted cursor-pointer select-none">
                                {t("agreeTerms") || "J'accepte les"} 
                                <span className="text-primary fw-bold ms-1" onClick={(e) => { e.preventDefault(); setIsModalOpen(true); }}>
                                    {t("learnMore") || "Termes & Conditions"}
                                </span>
                            </label>
                            <div className="text-danger small">{errors.agree?.message}</div>
                        </div>
                    </div>
                  </div>

                  {/* Bouton Submit */}
                  <div className="form-group">
                    <button
                      className="btn btn-gradient w-100 py-3 fw-bold text-white shadow-sm"
                      type="submit"
                      disabled={loading}
                    >
                      {loading ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2"></span>
                          Création en cours...
                        </>
                      ) : (
                        t("submitNRegister")
                      )}
                    </button>
                  </div>

                  <div className="text-center mt-4">
                    <span className="text-muted small">{t("alreadyHaveAccount")} </span>
                    <Link to="/login" className="fw-bold text-primary text-decoration-none">
                        {t("signIn")}
                    </Link>
                  </div>

                </form>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* MODAL Conditions Générales */}
      <Modal
        isOpen={isModalOpen}
        onRequestClose={() => setIsModalOpen(false)}
        style={modalStyles}
        contentLabel="Terms and Conditions"
      >
        <div className="modal-header border-bottom p-3 d-flex justify-content-between align-items-center bg-light">
            <h5 className="m-0 fw-bold text-dark">{t("textcon1") || "Conditions Générales"}</h5>
            <button className="btn-close" onClick={() => setIsModalOpen(false)}></button>
        </div>
        
        <div className="modal-body p-4" style={{maxHeight: '60vh', overflowY: 'auto'}}>
            <div className="terms-content">
                <h6>{t("textcon2")}</h6> <p className="text-muted small">{t("textcon3")}</p>
                <h6>{t("textcon4")}</h6> <p className="text-muted small">{t("textcon5")}</p>
                <h6>{t("textcon6")}</h6> <p className="text-muted small">{t("textcon7")}</p>
                <h6>{t("textcon8")}</h6> <p className="text-muted small">{t("textcon9")}</p>
                {/* Ajoutez les autres paragraphes ici */}
            </div>
        </div>

        <div className="modal-footer border-top p-3 bg-light text-end">
            <button 
                className="btn btn-primary btn-sm px-4" 
                onClick={() => {
                    // Optionnel : cocher automatiquement la case si l'utilisateur clique sur "J'accepte" dans le modal
                    // setValue('agree', true); 
                    setIsModalOpen(false);
                }}
            >
                {t("textconclose") || "Fermer"}
            </button>
        </div>
      </Modal>

    </div>
  );
};

export default Register;